import {
  InputText,
  Anchor,
  DataButton,
  Icon,
  InputContainer,
  ListItem,
  H4,
} from "@sellar-io/components";
import { Alert } from "../../../components/alert/Alert";
import { formatCurrency, pluralize } from "@sellar-io/common";
import { useNavigate } from "react-router";
import * as ROUTES from "../../../constants/routes";
import { useCustomerZonesUpdate } from "../../../hooks/api/v2/customerZones/useCustomerZonesUpdate";
import { useState } from "react";
import { useDebounce } from "use-debounce";
import { InfiniteList } from "../../../components/infiniteLoading/InfiniteList";
import { useOrderRulesList } from "../../../hooks/api/v2/orderRules/useOrderRulesList";
import { twMerge } from "tailwind-merge";

export const CustomerZoneOrderRules = ({
  customerZoneId,
  currentOrderRulesId,
}: {
  customerZoneId: number;
  currentOrderRulesId: number | null;
}) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string>();
  const [search, setSearch] = useState("");

  const [debouncedSearch] = useDebounce(search, 500);

  const orderRulesQuery = useOrderRulesList({
    search: debouncedSearch,
    limit: 10,
  });

  const orderRules = orderRulesQuery.data?.pages.flatMap((p) => p) ?? [];

  const customerZoneUpdate = useCustomerZonesUpdate(customerZoneId);

  const updateCustomerZone = (OrderRulesId: number, selected: boolean) => {
    const orderRule = orderRules.find((at) => at.id === OrderRulesId);

    customerZoneUpdate.mutate(
      {
        OrderRulesId: selected ? null : OrderRulesId,
      },
      {
        onError: () => {
          setError(
            `Failed to update customer zone with Order Rules: ${orderRule?.name}`,
          );
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-4 px-4">
      {error && <Alert type="error" title="Failed to update" message={error} />}

      <div className="flex flex-col gap-1 border-b border-zinc-300 pb-4">
        <H4 className="font-semibold">Order rules</H4>
        <p className="text-zinc-500">
          Create sets of minimum order rules based on either basket value,
          container quantity, or both. They can be applied to entire customer
          zones or individual customers. For more information or help setting up
          you order rules, visit our{" "}
          <Anchor
            className="inline-flex items-center gap-1 text-blue-600 underline"
            href="https://intercom.help/sellario/en/articles/9981916-supplier-guide-delivery-settings#h_d37178a34b"
            target="_blank"
            rel="noopener noreferrer"
          >
            help centre
            <Icon icon="ARROW_TOP_RIGHT" size={0.6} />
          </Anchor>
        </p>
      </div>

      <div className="flex items-center justify-between gap-2">
        <InputContainer className="w-full py-[0.4rem] md:max-w-[250px] md:flex-none">
          <InputText
            required={false}
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full"
          />
        </InputContainer>
        <div className="flex items-center gap-2">
          <DataButton
            className="flex items-center gap-1"
            border={true}
            onClick={() => navigate(ROUTES.ORDER_RULES)}
          >
            <Icon icon="PLUS" />
            New
          </DataButton>
        </div>
      </div>

      <InfiniteList
        query={orderRulesQuery}
        className="mb-8 flex flex-col gap-2"
      >
        {orderRules.map((orderRules) => {
          const selected = orderRules.id === currentOrderRulesId;

          return (
            <ListItem
              key={orderRules.id}
              title={orderRules.name}
              subtitle={
                <>
                  {!!orderRules.minimumOrderPrice && (
                    <span>
                      Basket subtotal{" "}
                      {formatCurrency(orderRules.minimumOrderPrice)},
                    </span>
                  )}
                  <span>
                    {orderRules.PriceTierOrderRules.length} per-container{" "}
                    {pluralize("rule", orderRules.PriceTierOrderRules.length)}
                  </span>
                </>
              }
              icon={selected ? "CHECK" : "CLIPBOARD_CHECK_OUTLINE"}
              actionText="View"
              onClick={() =>
                navigate(
                  ROUTES.ORDER_RULE.href({ orderRulesId: orderRules.id }),
                )
              }
              iconClick={() => updateCustomerZone(orderRules.id, selected)}
              className={twMerge(
                "rounded-lg bg-white p-4 hover:bg-zinc-50",
                selected ? "border border-zinc-300 bg-zinc-50" : "",
              )}
              iconClassName={selected ? "border-zinc-800 bg-zinc-200" : ""}
            />
          );
        })}
      </InfiniteList>
    </div>
  );
};
